/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */

import { Injectable, OnDestroy } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable, of, Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { isCloudUser, serverInfoSelector } from 'src/app/store/app.selectors';
import { UtilService } from '@services/util.service';
import { AppHttpService } from '@services/app-http.service';
import { isEmpty } from 'lodash-es';
import { ProductInfo, UserInfo } from '@foglight/angular-common';
@Injectable({
  providedIn: 'root',
})
export class AdminPageGuard implements CanActivate, OnDestroy {
  private destroy$ = new Subject<void>();
  isCloudUser = false;
  unauthorizedMessage = '';

  constructor(
    private router: Router,
    private store: Store,
    private util: UtilService,
    private httpService: AppHttpService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.store.select(serverInfoSelector).pipe(
      switchMap((serverInfo: ProductInfo) => {
        if (isEmpty(serverInfo)) {
          return this.verifyWithAPIData();
        } else {
          return this.verifyWithStoreData(serverInfo);
        }
      })
    );
  }

  verifyWithStoreData(serverInfo): Observable<boolean | UrlTree> {
    let isCloud = serverInfo.isCloud;
    if (isCloud) {
      return this.store.select(isCloudUser).pipe(
        switchMap((cloudUser: boolean) => {
          if (cloudUser) {
            return of(true);
          } else {
            this.unauthorizedMessage =
              'You do not have the required roles to access this page';
            sessionStorage.setItem(
              'lastErrorMessage',
              this.unauthorizedMessage
            );
            this.router.navigateByUrl('unauthorized');
          }
        })
      );
    } else {
      return of(true);
    }
  }

  verifyWithAPIData(): Observable<boolean | UrlTree> {
    return this.httpService.getProductInfo().pipe(
      switchMap((serverInfo) => {
        let isCloud = serverInfo.isCloud;
        if (isCloud) {
          return this.httpService.getUserInfo().pipe(
            takeUntil(this.destroy$),
            map((user: UserInfo) => {
              if (this.util.isCloudUser(user)) {
                return true;
              } else {
                this.unauthorizedMessage =
                  'You do not have the required roles to access this page';
                sessionStorage.setItem(
                  'lastErrorMessage',
                  this.unauthorizedMessage
                );
                this.router.navigateByUrl('unauthorized');
              }
            })
          );
        } else {
          return of(true);
        }
      })
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
